// PassengerPage.js - Premium Design
import React from 'react';
import { useBooking } from '../hooks/useBooking';
import Layout from '../components/layout/Layout';
import ProgressSteps from '../components/common/ProgressSteps';
import PassengerForm from '../components/booking/PassengerForm';
import BookingSummary from '../components/booking/BookingSummary';
import { PAGES, BOOKING_STEPS } from '../utils/constants';
import { formatCurrency } from '../utils/helpers';

const PassengerPage = () => {
  const {
    state: { searchData, selectedVehicle, extrasData, passengerData, routeInfo },
    setCurrentPage,
    setPassengerData
  } = useBooking();

  const handleContinue = (formData) => {
    setPassengerData(formData);
    setCurrentPage(PAGES.PAYMENT);
  };

  const handleBack = () => {
    setCurrentPage(PAGES.EXTRAS);
  };

  if (!searchData || !selectedVehicle) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-8 rounded-3xl text-center">
            <p className="text-gray-400 mb-4 font-medium">Session expired</p>
            <button
              onClick={() => setCurrentPage(PAGES.HOME)}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-black text-xs tracking-widest uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
            >
              Start Over
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const vehicleTotal = selectedVehicle?.pricing?.totalAmount || 0;
  const extrasTotal = extrasData?.extrasTotal || 0;
  const currency = selectedVehicle?.pricing?.currency || 'USD';

  return (
    <Layout showHeaderMobile={false}>
      <div className="min-h-screen pb-20 bg-[#050A12]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <ProgressSteps currentStep={BOOKING_STEPS.PASSENGER} />

          <div className="grid grid-cols-12 gap-8 mt-4">
            {/* Main Form Area */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              <div className="glass-panel p-6 md:p-10 rounded-[2.5rem] border-white/5 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600/0 via-blue-600/50 to-blue-600/0"></div>
                
                <div className="flex items-center gap-4 mb-8">
                  <button onClick={handleBack} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-all">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight italic uppercase">Passenger Details</h2>
                </div>

                <PassengerForm
                  initialData={passengerData}
                  onContinue={handleContinue}
                  onBack={handleBack}
                />
              </div>

              {/* Data Privacy Badge */}
              <div className="glass-panel bg-green-500/5 border-green-500/10 rounded-2xl p-5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 text-xl">🔒</div>
                <div>
                  <h4 className="font-black text-[10px] text-green-500 uppercase tracking-widest">Privacy Guaranteed</h4>
                  <p className="text-[11px] text-gray-500 mt-1 font-medium italic">Your personal information is encryptred and used only for service facilitation.</p>
                </div>
              </div>
            </div>

            {/* Sticky Sidebar */}
            <div className="hidden lg:block lg:col-span-4 h-fit sticky top-24">
              <BookingSummary
                searchData={searchData.searchFormData}
                selectedVehicle={selectedVehicle}
                extrasData={extrasData}
                step={BOOKING_STEPS.PASSENGER}
                routeInfo={routeInfo}
                onContinue={() => {
                  // Trigger the form submission from the sidebar
                  const el = document.getElementById('passenger-continue-btn');
                  if (el) el.click();
                }}
              />
            </div>
          </div>
        </div>

        {/* Mobile Fixed Confirmation Bar */}
        <div className="md:hidden fixed bottom-6 left-4 right-4 z-[100]">
          <div className="bg-[#0f172a]/95 backdrop-blur-2xl border border-white/10 rounded-full p-2 pl-6 flex items-center justify-between shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
            <div className="min-w-0">
              <p className="text-[9px] text-blue-500 font-black uppercase tracking-widest italic truncate">Details Ready</p>
              <p className="text-lg font-black text-white tracking-tighter leading-none mt-0.5">
                {formatCurrency(vehicleTotal + extrasTotal, currency)}
              </p>
            </div>
            <button 
              onClick={() => { const el = document.getElementById('passenger-continue-btn'); if (el) el.click(); }} 
              className="bg-blue-600 text-white h-12 px-8 rounded-full font-black text-xs uppercase tracking-[0.15em] transition-all active:scale-95 shadow-lg shadow-blue-500/20"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PassengerPage;